import React from 'react'
import { Link } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import AccountSettingsNav from '@/components/AccountSettingsNav'
import BusinessIdentityCard from '@/components/account/BusinessIdentityCard'
import RevenueBasisCard from '@/components/account/RevenueBasisCard'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useBrand } from '@/hooks/useBrand'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Building2, Sparkles, AlertTriangle, ArrowRight } from 'lucide-react'
import { formatShortWhen } from '@/lib/account-dates'
import type { AccountSettingsSharedProps, RevenueBasisInfo } from '@/types/shared'

interface OverviewAccount {
  id: number
  name: string
  /** The business's current web address — the segment in /a/…. Renaming carries it along. */
  address: string
  createdAt: string
  billingMode: 'internal' | 'commercial'
  plan: string | null
  subscriptionStatus: string
  liveAllowed: boolean
  memberCount: number
  propertyCount: number
}

interface OverviewPropertyRow {
  id: number
  name: string
  /**
   * This hotel's canonical path (Operator::AccountSettingsController#properties_summary). Note the
   * pre-slug link here pointed at `/properties/:id/dashboard`, which was never a route — the
   * dashboard IS the property root, and `basePath` is exactly that.
   */
  basePath: string
  dryRun: boolean
  lastComputationAt: string | null
  lastPushAt: string | null
  errorMessage: string | null
}

interface OverviewProps extends AccountSettingsSharedProps {
  account: OverviewAccount
  propertiesSummary: OverviewPropertyRow[]
  /** Every basis on offer, each with backend-authored copy. */
  revenueBasisOptions: RevenueBasisInfo[]
}

export default function Overview({
  currentUser,
  currentAccount,
  accountAbilities,
  activeSection,
  account,
  propertiesSummary,
  revenueBasis,
  revenueBasisOptions,
}: OverviewProps) {
  const brand = useBrand()

  return (
    <PortfolioLayout title="Business settings" currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Heading */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Business settings</h1>
            <HelpTooltip content="Your business identity, plan, team and properties in one place. What you can change here depends on your role." />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">Everything about this business — identity, plan and the properties it runs.</p>
        </section>

        <AccountSettingsNav accountAbilities={accountAbilities} activeSection={activeSection} settingsPath={currentAccount.settingsPath} />

        {/* Business identity — name AND web address, renamed together (see BusinessIdentityCard). */}
        <BusinessIdentityCard
          name={account.name}
          address={account.address}
          settingsPath={currentAccount.settingsPath}
          canManage={accountAbilities.manageAccount}
          memberCount={account.memberCount}
          propertyCount={account.propertyCount}
          createdAt={account.createdAt}
          animationDelay={100}
        />

        {/* Plan */}
        <section className="animate-slide-in" style={{ animationDelay: '150ms' }}>
          <div className="rounded-xl border border-border/60 bg-white shadow-sm">
            <div className="border-b border-border/40 px-5 py-4">
              <h2 className="text-sm font-semibold text-foreground">Plan</h2>
              <p className="text-xs text-muted-foreground">How this business is set up on {brand}.</p>
            </div>
            <div className="p-5">
              {account.billingMode === 'internal' ? (
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <Sparkles className="h-4 w-4 text-primary" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Complimentary access</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">This business is never billed.</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <Badge variant="outline" className="border-primary/30 bg-primary/5 text-[11px] capitalize text-primary">
                      {account.plan ?? 'No plan'}
                    </Badge>
                    <Badge variant="outline" className="border-border bg-muted text-[11px] capitalize text-muted-foreground">
                      {account.subscriptionStatus.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  {accountAbilities.manageBilling && (
                    <Link
                      href={`${currentAccount.settingsPath}/billing`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
                    >
                      Manage billing &amp; plan
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Revenue reporting basis */}
        <RevenueBasisCard settingsPath={currentAccount.settingsPath}
          current={revenueBasis}
          options={revenueBasisOptions}
          canManage={accountAbilities.manageAccount}
          animationDelay={200}
        />

        {/* Properties */}
        <section className="animate-slide-in" style={{ animationDelay: '250ms' }}>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Properties</h2>
            <HelpTooltip content={`Every property this business runs on ${brand}, with its publishing mode and latest pipeline activity.`} />
          </div>

          {propertiesSummary.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Building2 className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No properties yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Connect your first property to start managing rates.</p>
              <Link
                href="/onboarding"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-foreground/90"
              >
                Add property
              </Link>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Last computed</TableHead>
                    <TableHead className="text-right">Last pushed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propertiesSummary.map((p) => (
                    <React.Fragment key={p.id}>
                      <TableRow>
                        <TableCell>
                          <Link
                            href={p.basePath}
                            className="font-medium text-foreground transition-colors hover:text-primary"
                          >
                            {p.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {p.dryRun ? (
                            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[11px] text-amber-700">
                              Paused
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[11px] text-emerald-700">
                              Live
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                          {formatShortWhen(p.lastComputationAt)}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                          {formatShortWhen(p.lastPushAt)}
                        </TableCell>
                      </TableRow>
                      {p.errorMessage && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={4} className="bg-rose-50/60 py-2">
                            <span className="flex items-start gap-1.5 text-xs text-rose-700">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              {p.errorMessage}
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>
    </PortfolioLayout>
  )
}
